import { LDClient, initialize } from 'launchdarkly-js-client-sdk';
import { ConfigService } from '~/services/ConfigService/ConfigService';
import { logsService } from '~/services/LogsService';
import { isTrackingActive } from '~/hooks/track';
import qs from 'qs';
import { ExperimentInfo } from './models';
import { THIRD_PARTY_BOOT_TIMEOUT_MS, withTimeout } from '~/utils/withTimeout';

let ldClient: LDClient | undefined;
let initialToken: string | undefined;

const TRACKED_FEATURE_FLAGS = ['decline-skip-early-email-verification', 'payment-plans'] as const;

const setTrackedFeatureFlags = () => {
  if (!ldClient || !isTrackingActive) return;

  const featureFlags = TRACKED_FEATURE_FLAGS.reduce<Record<string, unknown>>((acc, flag) => {
    acc[flag] = ldClient!.variation(flag);
    return acc;
  }, {});

  logsService.setInfo({ featureFlags });
};

const evaluateExperiment = () => {
  if (!ldClient || !isTrackingActive) return;

  const flagName = ConfigService.config?.launchDarkly?.experiment?.flagName;
  if (!flagName) return;

  const detail = ldClient.variationDetail(flagName);
  if (detail?.reason?.inExperiment) {
    const experimentInfo: ExperimentInfo = {
      testName: flagName,
      testVariant: detail.value,
    };
    logsService.setInfo({ experimentInfo });
  }
};

export const initLaunchDarkly = async () => {
  if (!ldClient) {
    const { token } = qs.parse(window.location.search, { ignoreQueryPrefix: true }) as { token?: string };

    if (!token) {
      return;
    }

    initialToken = token;

    const context = {
      kind: 'loan-application',
      key: token,
    };

    ldClient = initialize(ConfigService.config?.launchDarklyKey, context);

    try {
      await withTimeout(
        ldClient.waitUntilReady(),
        THIRD_PARTY_BOOT_TIMEOUT_MS,
        '[LaunchDarkly][initLaunchDarkly] timed out waiting for client'
      );
    } catch (error) {
      console.error('[LaunchDarkly][initLaunchDarkly] failed to become ready', error);
    }

    setTrackedFeatureFlags();
  }
};

export const identifyRetailer = async ({
  retailerId,
  allianceId,
  merchantVertical,
}: {
  retailerId: number;
  allianceId?: number;
  merchantVertical: string;
}): Promise<void> => {
  if (!ldClient || !retailerId || !initialToken) return;

  await ldClient.identify({
    kind: 'multi',
    'loan-application': {
      key: initialToken,
      vertical: merchantVertical,
    },
    retailer: {
      key: String(retailerId),
      ...(allianceId ? { allianceId } : {}),
      vertical: merchantVertical,
    },
  });

  setTrackedFeatureFlags();
  evaluateExperiment();
};

export const launchDarklyClient = {
  async trackEvent<T>(eventName: string, data?: T, flush: boolean = false) {
    ldClient?.track(eventName, data);
    if (flush) {
      await ldClient?.flush();
    }
  },
};

export const getFeatureFlagValue = (flag: string, defaultValue: boolean = false): boolean => {
  if (!ldClient || !flag) {
    return defaultValue;
  }
  return ldClient.variation(flag, defaultValue);
};

export const getFeatureFlagVariation = <T>(flag: string, defaultValue: T): T => {
  if (!ldClient || !flag) {
    return defaultValue;
  }
  return ldClient.variation(flag, defaultValue) as T;
};
